import { SubscriptionTier } from "src/data/landing/queries/subscriptionTiers";
import { tierGrantsCapability } from "src/business/subscription/tierPerks";

/**
 * The tier a locked feature card should send the restaurateur to: the cheapest
 * catalog tier bundling the missing capability, or `null` when none does.
 *
 * The catalog is cumulative by priority — a higher tier includes a lower one's
 * capabilities — so several tiers can bundle the same one. Comparing on
 * `monthlyPrice` rather than `priority` gives the same answer today and the
 * right one the moment a tier is repriced: price is what the restaurateur is
 * being asked for.
 */
export const resolveUpgradeTierFor = (
  capability: string,
  catalog: SubscriptionTier[] | undefined,
): SubscriptionTier | null =>
  (catalog ?? [])
    .filter((tier) => tierGrantsCapability(tier, capability))
    .reduce<SubscriptionTier | null>(
      (cheapest, tier) =>
        !cheapest || tier.monthlyPrice < cheapest.monthlyPrice ? tier : cheapest,
      null,
    );

/** Catalog tiers that are a genuine upgrade from the restaurant's current one. */
export const upgradeableTiers = (
  catalog: SubscriptionTier[] | undefined,
  currentTier: string | null,
): SubscriptionTier[] => {
  const tiers = catalog ?? [];
  const current = tiers.find((tier) => tier.name === currentTier);
  // With no subscription record every tier is reachable — the backend derives
  // nothing to compare against.
  const floor = current?.priority ?? Number.NEGATIVE_INFINITY;

  return tiers
    .filter((tier) => tier.priority > floor)
    .sort((a, b) => a.priority - b.priority);
};
